<template>
  <div>
    <v-text-field
      v-model="search"
      class="search-field mb-2 mt-0"
      append-icon="search"
      label="Search"
      single-line
      hide-details
    />
    <v-data-table
      :headers="headers"
      :items="items"
      :items-per-page.sync="pagination.rowsPerPage"
      :sort-by.sync="pagination.sortBy"
      :search="search"
    >
      <template #item="props">
        <tr>
          <td class="body-2 font-weight-medium">
            {{ props.item.name }}
          </td>
          <td class="body-2">{{ props.item.email }}</td>
          <td class="body-2">
            {{ getRoleNamesFromMemberships(props.item.memberships) }}
          </td>
          <td class="body-2">
            <v-chip
              :color="getColorForStatus(props.item.status)"
              small
              text-color="white"
              class="font-weight-medium"
            >
              {{ props.item.status }}
            </v-chip>
          </td>
          <td v-if="displayHiddenToggle">
            <v-checkbox v-model="props.item.hidden" @change="toggleHidden(props.item, $event)" />
          </td>
          <td>
            <resend-invite-dialog
              v-if="props.item.status === 'Invited'"
              :email="props.item.email"
              :username="props.item.user.id"
              @invite-resent="email => $emit('invite-resent', email)"
            />
          </td>
          <td>
            <manage-user-roles-dialog :user-memberships="props.item" />
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import _ from 'lodash'
import ManageUserRolesDialog from '@/components/manage/ManageUserRolesDialog'
import ResendInviteDialog from '@/components/manage/ResendInviteDialog'
import { roleService } from '@/services/role-service'
import { permissionsMixin } from '@/mixins/permissions'
import { MyActiveOrganization } from '@/helpers/my-account-helper'

export default {
  components: { ResendInviteDialog, ManageUserRolesDialog },
  props: {
    memberships: { type: Object, required: true },
    users: { type: Object, required: true }
  },
  data() {
    return {
      search: '',
      pagination: {
        rowsPerPage: 10,
        sortBy: ['name']
      },
      roles: {}
    }
  },
  computed: {
    items() {
      return _.map(this.users, user => {
        const userMemberships = this.getMembershipsForUser(user.username)
        const userObject = this.getUserByUsername(user.username)
        return {
          memberships: userMemberships,
          user: userObject,
          name: `${userObject.firstName} ${userObject.lastName}`,
          email: userObject.email,
          status: userObject.emailVerified
            ? _.isEmpty(userMemberships)
              ? 'Deactivated'
              : 'Active'
            : 'Invited',
          hidden: this.isHidden(userMemberships)
        }
      })
    },
    membershipsByUser() {
      return _.groupBy(this.memberships, it => it.user)
    },
    displayAdminRole() {
      return permissionsMixin.methods.hasAnyPermission([
        'MEMBERSHIP_ADMINISTRATOR_CREATE',
        'MEMBERSHIP_ADMINISTRATOR_DELETE'
      ])
    },
    displayRealTimeFeasibilityReviewerRole() {
      return permissionsMixin.methods.hasAnyPermission([
        'DEEP_LENS_COORDINATOR_CREATE',
        'DEEP_LENS_COORDINATOR_DELETE'
      ])
    },
    displayHiddenMemberships() {
      return permissionsMixin.methods.hasAnyPermission(['MEMBERSHIP_HIDE_READ'])
    },
    displayHiddenToggle() {
      return permissionsMixin.methods.hasAnyPermission([
        'MEMBERSHIP_HIDE_CREATE',
        'MEMBERSHIP_HIDE_DELETE'
      ])
    },
    headers() {
      return [
        {
          text: 'User',
          align: 'left',
          sortable: true,
          value: 'name'
        },
        {
          text: 'Email Address',
          align: 'left',
          sortable: true,
          value: 'email'
        },
        {
          text: 'Access',
          align: 'left',
          sortable: false,
          value: 'userRole',
          width: '50%'
        },
        {
          text: 'Status',
          align: 'left',
          sortable: true,
          value: 'status'
        },
        {
          text: 'Hide',
          sortable: false,
          class: this.displayHiddenToggle ? 'toggle-hide' : 'd-none'
        },
        // empty header for resend invite button
        {
          sortable: false
        },
        // empty header for edit button
        {
          sortable: false
        }
      ]
    }
  },
  async created() {
    this.roles = _.mapValues(_.keyBy((await roleService.list()).data, 'id'), 'name')
  },
  methods: {
    getColorForStatus(status) {
      if (status === 'Pending') {
        return 'warning'
      } else if (status === 'Active') {
        return 'success'
      } else {
        return this.$vuetify.theme.themes.light.dark
      }
    },
    getMembershipsForUser(username) {
      return this.membershipsByUser[username] || []
    },
    getRoleNamesFromMemberships(memberships) {
      let permittedMemberships = _.filter(
        memberships,
        membership => membership.userRole !== 'HIDDEN'
      )
      if (!this.displayAdminRole) {
        permittedMemberships = _.reject(permittedMemberships, membership => {
          return membership.userRole === 'ADMINISTRATOR'
        })
      }
      if (!this.displayRealTimeFeasibilityReviewerRole) {
        permittedMemberships = _.reject(permittedMemberships, membership => {
          return membership.userRole === 'DEEP_LENS_COORDINATOR'
        })
      }
      return _.join(
        _.sortBy(_.map(permittedMemberships, it => _.startCase(this.roles[it.userRole]))),
        ', '
      )
    },
    getUserByUsername(username) {
      return _.get(this.users, username)
    },
    isHidden(memberships) {
      return !_.isNil(_.find(memberships, membership => membership.userRole === 'HIDDEN'))
    },
    async toggleHidden(userMembership, event) {
      let roles = _.map(userMembership.memberships, membership => membership.userRole)
      if (event) {
        roles = _.concat(roles, 'HIDDEN')
      } else {
        roles = _.filter(roles, role => role !== 'HIDDEN')
      }
      await this.$store.dispatch('UPDATE_MEMBERSHIPS', {
        organizationId: MyActiveOrganization.id,
        username: userMembership.user.username,
        roles: roles
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.search-field {
  max-width: 350px;
}
</style>
